import * as React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SplitButton } from '@/components/ui/split-button';
import { Separator } from '@/components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  Download,
  ScrollText,
  FileCheck,
  Plus,
  FileType,
  Eye,
  Edit,
  Unlink,
  MoreVertical,
  Mail
} from 'lucide-react';
import { useFileDownload } from '@/lib/services/files/hooks';
import { CertifiedCopyBadge } from '@/components/ui/certified-copy-badge';
import { FILE_TYPE_BADGE_LABEL } from './file-type-badge-label';
import { toast } from '@/lib/toast';

type DocFileTypeMeta = {
  slug?: string;
  name?: string;
  category?: string;
};

/**
 * Display shape accepted by DocumentList. Structural superset of
 * `WorkflowDocument` and the legacy additional-doc shape used in some
 * callers — every field is optional except `id` so the props accept both
 * payload variants without losing type-safety.
 */
export interface DisplayDocument {
  id: number;
  fileType?: string | DocFileTypeMeta;
  type?: DocFileTypeMeta;
  originalFilename?: string;
  displayName?: string;
  fileName?: string;
  name?: string;
  fileUrl?: string;
  url?: string;
  isCertified?: boolean;
  isCertifiedCopy?: boolean;
}

function getFileTypeMeta(doc: DisplayDocument): DocFileTypeMeta | undefined {
  if (doc.fileType && typeof doc.fileType === 'object') return doc.fileType;
  if (doc.type) return doc.type;
  return undefined;
}

interface DocumentListProps {
  documents: DisplayDocument[];
  additionalDocs: DisplayDocument[];
  institutionName: string;
  matterId?: number;
  correspondenceId?: number;
  letterContent?: string;
  layout?: 'column' | 'row';
  onAddAttachment?: () => void;
  onRemoveAttachment?: (
    fileId: string,
    isCorrespondenceAttachment?: boolean
  ) => void;
  onLetterClick?: () => void;
  hideLetterComponent?: boolean; // Hide the letter component
  showEmailComponent?: boolean; // Show email instead of letter
  emailSubject?: string;
  emailBody?: string;
  readOnly?: boolean; // Disable edit/remove actions (e.g. pending research)
}

export const DocumentList: React.FC<DocumentListProps> = ({
  documents,
  additionalDocs,
  institutionName,
  matterId,
  correspondenceId,
  letterContent: _letterContent,
  layout = 'column',
  onAddAttachment,
  onRemoveAttachment,
  onLetterClick,
  hideLetterComponent = false,
  showEmailComponent = false,
  emailSubject: _emailSubject = '',
  emailBody: _emailBody = '',
  readOnly = false
}) => {
  // Debug logging to identify duplicate attachments
  if (process.env.NODE_ENV === 'development') {
    const debugDoc = (d: DisplayDocument) => ({
      id: d.id,
      type: d.type ?? d.fileType,
      fileName: d.fileName ?? d.name ?? d.originalFilename ?? d.displayName
    });
    console.log('DocumentList Debug:', {
      documentsCount: documents.length,
      additionalDocsCount: additionalDocs.length,
      documents: documents.map(debugDoc),
      additionalDocs: additionalDocs.map(debugDoc)
    });
  }

  // Show all documents since we're now using correspondence attachments directly
  const uploadedDocs = documents;
  const _requiresWill = institutionName.includes('NAB');
  const _totalAttachments = uploadedDocs.length + additionalDocs.length;

  // Use real file download hook
  const downloadMutation = useFileDownload(0); // matterId not needed for workflow docs

  const handleDownload = async (
    fileName: string,
    url?: string,
    fileId?: number
  ) => {
    console.log(`Downloading ${fileName}...`, { fileName, url, fileId });

    // Special handling for Letter
    if (fileName === 'Letter') {
      if (!correspondenceId || !matterId) {
        toast.error('Cannot download Letter: missing correspondence details');
        return;
      }

      try {
        // Generate the PDF URL
        // This downloads the currently saved version without saving any pending changes
        const pdfUrl = `/api/proxy/matters/${matterId}/correspondences/${correspondenceId}/preview-letter`;

        // Create a link element to trigger download
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = `Discovery_Letter_${institutionName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        toast.success('Letter downloaded');
      } catch (error) {
        console.error('Failed to download Letter:', error);
        toast.error('Failed to download Letter');
      }
      return;
    }

    // Regular document handling - file should have an ID for download
    if (fileId) {
      console.log('Downloading file with ID:', fileId);
      // Use the file download mutation for real files
      downloadMutation.mutate({ fileId, preview: false });
    } else if (url) {
      // Fallback for files with direct URLs
      console.log('Opening URL for download:', url);
      window.open(url, '_blank');
    } else {
      // This shouldn't happen since workflow documents should have IDs
      console.error('No file ID or URL available for download');
      toast.error('Unable to download file - missing file information');
    }
  };

  const handlePreview = async (
    fileName: string,
    url?: string,
    fileId?: number
  ) => {
    console.log(`Previewing ${fileName}...`, { fileName, url, fileId });

    // Special handling for Letter
    if (fileName === 'Letter') {
      if (!correspondenceId || !matterId) {
        toast.error('Cannot preview Letter: missing correspondence details');
        return;
      }

      try {
        // Open the PDF in a new tab using the preview-letter endpoint
        // This shows the currently saved version without saving any pending changes
        const pdfUrl = `/api/proxy/matters/${matterId}/correspondences/${correspondenceId}/preview-letter`;
        window.open(pdfUrl, '_blank');
      } catch (error) {
        console.error('Failed to preview Letter:', error);
        toast.error('Failed to open Letter preview');
      }
      return;
    }

    // Regular document handling - file should have an ID for preview
    if (fileId) {
      console.log('Previewing file with ID:', fileId);
      // Use the file download mutation for preview
      downloadMutation.mutate({ fileId, preview: true });
    } else if (url) {
      // Fallback for files with direct URLs
      console.log('Opening URL directly:', url);
      window.open(url, '_blank');
    } else {
      // This shouldn't happen since workflow documents should have IDs
      console.error('No file ID or URL available for preview');
      toast.error('Unable to preview file - missing file information');
    }
  };

  const getDocumentIcon = (doc: DisplayDocument) => {
    const meta = getFileTypeMeta(doc);
    if (meta?.slug) {
      switch (meta.slug) {
        case 'death-certificate':
          return FileCheck;
        case 'will':
        case 'grant-of-probate':
          return ScrollText;
        default:
          return FileType;
      }
    }

    // Fallback to filename checking for legacy data
    const fileName = getFileName(doc);
    const lowerName = fileName.toLowerCase();

    if (lowerName.includes('will')) {
      return ScrollText;
    } else if (lowerName.includes('death certificate')) {
      return FileCheck;
    }

    // Default to PDF icon as most legal documents are PDFs
    return FileType;
  };

  const getFileTypeBadge = (doc: DisplayDocument) => {
    const meta = getFileTypeMeta(doc);
    if (!meta?.name) return null;
    const label =
      FILE_TYPE_BADGE_LABEL[meta.slug ?? ''] ??
      (meta.category === 'DECEDENTS_ASSETS' ? meta.name : null);
    if (!label) return null;
    return <Badge variant="secondary">{label}</Badge>;
  };

  const getFileName = (doc: DisplayDocument): string => {
    // Use raw filename without pretty printing - prioritize display name
    if (doc.displayName && doc.displayName.trim()) {
      return doc.displayName.trim();
    }
    if (doc.originalFilename && doc.originalFilename.trim()) {
      return doc.originalFilename.trim();
    }
    if (doc.fileName && doc.fileName.trim()) {
      return doc.fileName.trim();
    }
    if (doc.name && doc.name.trim()) {
      return doc.name.trim();
    }

    return doc.id ? `document-${doc.id}.pdf` : 'document.pdf';
  };

  if (layout === 'row') {
    return (
      <div className="space-y-3">
        {/* Letter PDF or Email - Separated from attachments */}
        {!hideLetterComponent && (
          <div className="flex flex-wrap items-center gap-2">
            {showEmailComponent ? (
              /* Simple Edit Email button for email-only mode */
              <Button variant="outline" size="2xs" onClick={onLetterClick}>
                <Mail className="mr-1.5 h-3 w-3" />
                Edit Email
              </Button>
            ) : (
              /* Full Letter component with dropdown for standard mode */
              <DropdownMenu>
                <SplitButton
                  variant="outline"
                  size="sm"
                  primaryAction={{
                    children: (
                      <>
                        <Edit className="mr-1.5 h-3 w-3" />
                        <span className="max-w-[150px] truncate">Letter</span>
                      </>
                    ),
                    onClick: readOnly
                      ? () => handlePreview('Letter')
                      : (onLetterClick ?? (() => {}))
                  }}
                  menuTrigger={{
                    children: (
                      <DropdownMenuTrigger asChild>
                        <span>
                          <MoreVertical className="h-3 w-3" />
                        </span>
                      </DropdownMenuTrigger>
                    )
                  }}
                />
                <DropdownMenuContent align="start" className="w-40">
                  <DropdownMenuItem onClick={() => handlePreview('Letter')}>
                    <Eye className="mr-2 h-3 w-3" />
                    Preview
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleDownload('Letter')}>
                    <Download className="mr-2 h-3 w-3" />
                    Download
                  </DropdownMenuItem>
                  {!readOnly && (
                    <DropdownMenuItem onClick={onLetterClick}>
                      <Edit className="mr-2 h-3 w-3" />
                      Edit
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        )}

        {/* Separator - only show if letter is visible */}
        {!hideLetterComponent && <Separator />}

        {/* Attachments */}
        <div className="space-y-2">
          <h4 className="text-muted-foreground text-sm font-medium">
            Attachments
          </h4>
          <div className="flex flex-wrap items-center gap-2">
            {/* Standard Documents */}
            {uploadedDocs.map((doc) => {
              const fileName = getFileName(doc);
              const fileTypeBadge = getFileTypeBadge(doc);
              return (
                <DropdownMenu key={doc.id}>
                  <SplitButton
                    variant="outline"
                    size="sm"
                    primaryAction={{
                      children: (
                        <>
                          {React.createElement(getDocumentIcon(doc), {
                            className: 'mr-1.5 h-3 w-3'
                          })}
                          <span className="max-w-[150px] truncate">
                            {fileName}
                          </span>
                          {fileTypeBadge}
                          {(doc.isCertified || doc.isCertifiedCopy) && (
                            <CertifiedCopyBadge compact />
                          )}
                        </>
                      ),
                      onClick: () =>
                        handlePreview(fileName, doc.fileUrl, doc.id)
                    }}
                    menuTrigger={{
                      children: (
                        <DropdownMenuTrigger asChild>
                          <span>
                            <MoreVertical className="h-3 w-3" />
                          </span>
                        </DropdownMenuTrigger>
                      )
                    }}
                  />
                  <DropdownMenuContent align="start" className="w-40">
                    <DropdownMenuItem
                      onClick={() =>
                        handlePreview(fileName, doc.fileUrl, doc.id)
                      }
                    >
                      <Eye className="mr-2 h-3 w-3" />
                      Preview
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() =>
                        handleDownload(fileName, doc.fileUrl, doc.id)
                      }
                    >
                      <Download className="mr-2 h-3 w-3" />
                      Download
                    </DropdownMenuItem>
                    {!readOnly && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => {
                            console.log('Unlinking:', fileName);
                            if (onRemoveAttachment) {
                              onRemoveAttachment(doc.id.toString(), true); // true = correspondence attachment
                            }
                          }}
                          className="text-destructive focus:text-destructive"
                        >
                          <Unlink className="mr-2 h-3 w-3" />
                          Remove
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            })}

            {/* Additional Documents */}
            {additionalDocs.map((doc) => {
              const fileName = getFileName(doc);
              const fileTypeBadge = getFileTypeBadge(doc);
              return (
                <DropdownMenu key={doc.id}>
                  <SplitButton
                    variant="outline"
                    size="sm"
                    primaryAction={{
                      children: (
                        <>
                          {React.createElement(getDocumentIcon(doc), {
                            className: 'mr-1.5 h-3 w-3'
                          })}
                          <span className="max-w-[150px] truncate">
                            {fileName}
                          </span>
                          {fileTypeBadge}
                          {doc.isCertifiedCopy && (
                            <CertifiedCopyBadge compact />
                          )}
                        </>
                      ),
                      onClick: () =>
                        handlePreview(fileName, doc.url ?? '', doc.id)
                    }}
                    menuTrigger={{
                      children: (
                        <DropdownMenuTrigger asChild>
                          <span>
                            <MoreVertical className="h-3 w-3" />
                          </span>
                        </DropdownMenuTrigger>
                      )
                    }}
                  />
                  <DropdownMenuContent align="start" className="w-40">
                    <DropdownMenuItem
                      onClick={() =>
                        handlePreview(fileName, doc.url ?? '', doc.id)
                      }
                    >
                      <Eye className="mr-2 h-3 w-3" />
                      Preview
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() =>
                        handleDownload(fileName, doc.url ?? '', doc.id)
                      }
                    >
                      <Download className="mr-2 h-3 w-3" />
                      Download
                    </DropdownMenuItem>
                    {!readOnly && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => {
                            console.log('Unlinking:', fileName);
                            if (onRemoveAttachment) {
                              onRemoveAttachment(doc.id.toString(), false); // false = additional doc
                            }
                          }}
                          className="text-destructive focus:text-destructive"
                        >
                          <Unlink className="mr-2 h-3 w-3" />
                          Remove
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            })}

            {/* Add attachment button */}
            {onAddAttachment && !readOnly && (
              <Button
                variant="outline"
                size="icon-xs"
                tone="accent-fill"
                onClick={onAddAttachment}
                title="Add attachment"
                // Icon-only trigger: `title` alone is not reliably announced by
                // screen readers, so name it explicitly (LF-6288).
                aria-label="Add attachment"
              >
                <Plus className="h-3 w-3" />
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {/* Standard Documents */}
      {uploadedDocs.map((doc) => {
        const fileName = getFileName(doc);
        const fileTypeBadge = getFileTypeBadge(doc);
        return (
          <div key={doc.id} className="flex items-center gap-2 py-1 text-sm">
            {React.createElement(getDocumentIcon(doc), {
              className: 'text-muted-foreground h-3 w-3'
            })}
            <span className="text-muted-foreground flex-1">{fileName}</span>
            {fileTypeBadge}
            {(doc.isCertified || doc.isCertifiedCopy) && (
              <Badge variant="outline" className="ml-2">
                Certified
              </Badge>
            )}
          </div>
        );
      })}

      {/* Additional Documents */}
      {additionalDocs.map((doc) => {
        const fileName = getFileName(doc);
        const fileTypeBadge = getFileTypeBadge(doc);
        return (
          <div key={doc.id} className="flex items-center gap-2 py-1 text-sm">
            {React.createElement(getDocumentIcon(doc), {
              className: 'text-muted-foreground h-3 w-3'
            })}
            <span className="text-muted-foreground flex-1">{fileName}</span>
            {fileTypeBadge}
            <Badge variant="secondary" className="ml-2">
              Additional
            </Badge>
          </div>
        );
      })}
    </div>
  );
};
